/**
 * Pure occupancy-grid (.pgm, binary P5) helpers for the 2D map cleanup editor
 * (ADR 102). Raster values follow the ROS map_server convention: 0 = occupied
 * (wall), 254/255 = free floor, 205 = unknown/unexplored. Edits change cell
 * VALUES only — dimensions and the yaml frame (origin/resolution) are
 * invariants, so a rebuilt GLB stays twin-frame at identity.
 */

export const OCCUPIED = 0;
export const FREE = 254;
export const UNKNOWN = 205;

export interface Pgm {
  width: number;
  height: number;
  maxval: number;
  data: Uint8Array; // row-major, length = width * height
}

export function parsePgm(bytes: Uint8Array): Pgm {
  if (bytes.length < 2 || bytes[0] !== 0x50 || bytes[1] !== 0x35) {
    throw new Error("not a binary (P5) pgm");
  }
  let i = 2;
  const vals: number[] = [];
  while (vals.length < 3 && i < bytes.length) {
    const c = bytes[i];
    if (c === 0x23) {
      while (i < bytes.length && bytes[i] !== 0x0a) i++; // '#' comment to EOL
    } else if (c === 0x20 || c === 0x09 || c === 0x0a || c === 0x0d) {
      i++;
    } else if (c >= 0x30 && c <= 0x39) {
      let v = 0;
      while (i < bytes.length && bytes[i] >= 0x30 && bytes[i] <= 0x39) {
        v = v * 10 + (bytes[i] - 0x30);
        i++;
      }
      vals.push(v);
    } else {
      throw new Error("bad pgm header byte");
    }
  }
  if (vals.length < 3) throw new Error("truncated pgm header");
  i++; // exactly one whitespace byte separates the header from the raster
  const [width, height, maxval] = vals;
  const data = bytes.slice(i, i + width * height);
  if (data.length < width * height) throw new Error("truncated pgm raster");
  return { width, height, maxval, data };
}

export function encodePgm(p: Pgm): Uint8Array {
  const header = new TextEncoder().encode(`P5\n${p.width} ${p.height}\n${p.maxval}\n`);
  const out = new Uint8Array(header.length + p.data.length);
  out.set(header, 0);
  out.set(p.data, header.length);
  return out;
}

/** Paint a filled disk of raster value `v` (the brush). */
export function paintDisk(p: Pgm, cx: number, cy: number, r: number, v: number): void {
  const r2 = r * r;
  const x0 = Math.max(0, Math.floor(cx - r));
  const x1 = Math.min(p.width - 1, Math.ceil(cx + r));
  const y0 = Math.max(0, Math.floor(cy - r));
  const y1 = Math.min(p.height - 1, Math.ceil(cy + r));
  for (let y = y0; y <= y1; y++) {
    for (let x = x0; x <= x1; x++) {
      const dx = x - cx;
      const dy = y - cy;
      if (dx * dx + dy * dy <= r2) p.data[y * p.width + x] = v;
    }
  }
}

/** Everything OUTSIDE the rectangle becomes `v` (crop-to-rectangle; size unchanged). */
export function fillOutsideRect(
  p: Pgm, rx0: number, ry0: number, rx1: number, ry1: number, v: number,
): void {
  const x0 = Math.min(rx0, rx1);
  const x1 = Math.max(rx0, rx1);
  const y0 = Math.min(ry0, ry1);
  const y1 = Math.max(ry0, ry1);
  for (let y = 0; y < p.height; y++) {
    for (let x = 0; x < p.width; x++) {
      if (x < x0 || x > x1 || y < y0 || y > y1) p.data[y * p.width + x] = v;
    }
  }
}

/**
 * Wall simplifier (ADR 130). SLAM occupancy grids render a plain wall as a
 * ragged cloud of black dots with pinholes and 1-cell gaps. This fuses that
 * cloud into a solid line, then wipes leftover isolated specks — a binary
 * morphological CLOSE (dilate → erode, bridges ≤1-cell gaps and fills pinholes
 * without thickening solid walls) followed by a conservative DESPECKLE (drop
 * occupied cells that are still fully isolated). Values only: the raster size
 * and map frame are invariant, so poses stay valid. A removed speck blends into
 * its surroundings — FREE if it sat in explored floor, UNKNOWN in the void —
 * which both extrude to no wall, so the difference is only cosmetic in the 2D view.
 */
const OCC_THRESHOLD = 128; // ROS map_server: 0 occupied · 205 unknown · 254 free

function occupancyMask(p: Pgm): Uint8Array {
  const m = new Uint8Array(p.width * p.height);
  for (let i = 0; i < m.length; i++) m[i] = p.data[i] < OCC_THRESHOLD ? 1 : 0;
  return m;
}

/** True if any 8-neighbour (self excluded) is set; out-of-bounds counts as unset. */
function anyNeighbour(m: Uint8Array, w: number, h: number, x: number, y: number): boolean {
  for (let dy = -1; dy <= 1; dy++) {
    for (let dx = -1; dx <= 1; dx++) {
      if (dx === 0 && dy === 0) continue;
      const nx = x + dx, ny = y + dy;
      if (nx < 0 || nx >= w || ny < 0 || ny >= h) continue;
      if (m[ny * w + nx]) return true;
    }
  }
  return false;
}

/** True if every 8-neighbour (self excluded) is set; out-of-bounds counts as unset. */
function allNeighbours(m: Uint8Array, w: number, h: number, x: number, y: number): boolean {
  for (let dy = -1; dy <= 1; dy++) {
    for (let dx = -1; dx <= 1; dx++) {
      if (dx === 0 && dy === 0) continue;
      const nx = x + dx, ny = y + dy;
      if (nx < 0 || nx >= w || ny < 0 || ny >= h) return false;
      if (!m[ny * w + nx]) return false;
    }
  }
  return true;
}

function dilate(m: Uint8Array, w: number, h: number): Uint8Array {
  const out = new Uint8Array(m.length);
  for (let y = 0; y < h; y++)
    for (let x = 0; x < w; x++)
      out[y * w + x] = m[y * w + x] || anyNeighbour(m, w, h, x, y) ? 1 : 0;
  return out;
}

function erode(m: Uint8Array, w: number, h: number): Uint8Array {
  const out = new Uint8Array(m.length);
  for (let y = 0; y < h; y++)
    for (let x = 0; x < w; x++)
      out[y * w + x] = m[y * w + x] && allNeighbours(m, w, h, x, y) ? 1 : 0;
  return out;
}

/** Fuse dotted walls into solid lines and drop isolated specks (in place). */
export function simplifyWalls(p: Pgm): void {
  const { width: w, height: h, data } = p;
  const original = occupancyMask(p);

  // CLOSE: bridge ≤1-cell gaps and fill pinholes without thickening walls.
  const closed = erode(dilate(original, w, h), w, h);

  // DESPECKLE: on the closed mask, drop cells that are still fully isolated
  // (a real dotted wall is now connected, so only true noise remains lone).
  const finalMask = closed.slice();
  for (let y = 0; y < h; y++)
    for (let x = 0; x < w; x++)
      if (closed[y * w + x] && !anyNeighbour(closed, w, h, x, y)) finalMask[y * w + x] = 0;

  // Write values only: newly-solid → OCCUPIED; dropped speck blends into its
  // background (FREE if any neighbour is floor, else UNKNOWN); rest kept.
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const i = y * w + x;
      const was = original[i] === 1;
      const now = finalMask[i] === 1;
      if (now && !was) data[i] = OCCUPIED;
      else if (was && !now) data[i] = blendBackground(data, w, h, x, y);
    }
  }
}

/** The value a cleared wall cell should take: FREE if it borders explored floor,
 * else UNKNOWN — both extrude to no wall, so this is only cosmetic in the 2D view. */
function blendBackground(data: Uint8Array, w: number, h: number, x: number, y: number): number {
  for (let dy = -1; dy <= 1; dy++) {
    for (let dx = -1; dx <= 1; dx++) {
      if (dx === 0 && dy === 0) continue;
      const nx = x + dx, ny = y + dy;
      if (nx < 0 || nx >= w || ny < 0 || ny >= h) continue;
      if (data[ny * w + nx] >= 220) return FREE; // FREE (254), not UNKNOWN (205) or OCCUPIED (0)
    }
  }
  return UNKNOWN;
}

/** Perpendicular distance from (px,py) to segment (x0,y0)-(x1,y1). */
function distToSegment(px: number, py: number, x0: number, y0: number, x1: number, y1: number): number {
  const vx = x1 - x0, vy = y1 - y0;
  const wx = px - x0, wy = py - y0;
  const c1 = vx * wx + vy * wy;
  if (c1 <= 0) return Math.hypot(px - x0, py - y0);
  const c2 = vx * vx + vy * vy;
  if (c2 <= c1) return Math.hypot(px - x1, py - y1);
  const b = c1 / c2;
  return Math.hypot(px - (x0 + b * vx), py - (y0 + b * vy));
}

/**
 * Straighten a wall to a drawn line (ADR 132). Everything within `clearBand`
 * cells of the segment is wiped to background (removing the ragged real wall),
 * then a clean straight wall of full width `thickness` is stamped along it.
 * Values only — size/frame invariant.
 */
export function straightenToLine(
  p: Pgm, x0: number, y0: number, x1: number, y1: number, thickness: number, clearBand: number,
): void {
  const { width: w, height: h, data } = p;
  const half = Math.max(0.5, thickness / 2);
  const pad = Math.ceil(clearBand + half) + 1;
  const minX = Math.max(0, Math.floor(Math.min(x0, x1) - pad));
  const maxX = Math.min(w - 1, Math.ceil(Math.max(x0, x1) + pad));
  const minY = Math.max(0, Math.floor(Math.min(y0, y1) - pad));
  const maxY = Math.min(h - 1, Math.ceil(Math.max(y0, y1) + pad));
  for (let y = minY; y <= maxY; y++) {
    for (let x = minX; x <= maxX; x++) {
      const d = distToSegment(x, y, x0, y0, x1, y1);
      const i = y * w + x;
      if (d <= half) data[i] = OCCUPIED;
      else if (d <= clearBand && data[i] < OCC_THRESHOLD) data[i] = blendBackground(data, w, h, x, y);
    }
  }
}

/**
 * Normalize every wall to one uniform thickness (ADR 132): Zhang–Suen thinning
 * reduces walls to a 1-px centerline (killing the thick/thin unevenness and
 * the dense blobs), then a re-dilate restores a fixed wall width of
 * `2·halfWidth+1` cells. Values only — size/frame invariant.
 */
export function uniformThickness(p: Pgm, halfWidth = 1): void {
  const { width: w, height: h, data } = p;
  const m = occupancyMask(p);

  // Zhang–Suen: iterate two sub-passes until no cell is removed.
  let changed = true;
  while (changed) {
    changed = false;
    for (let step = 0; step < 2; step++) {
      const toClear: number[] = [];
      for (let y = 1; y < h - 1; y++) {
        for (let x = 1; x < w - 1; x++) {
          const i = y * w + x;
          if (!m[i]) continue;
          const p2 = m[i - w], p3 = m[i - w + 1], p4 = m[i + 1], p5 = m[i + w + 1];
          const p6 = m[i + w], p7 = m[i + w - 1], p8 = m[i - 1], p9 = m[i - w - 1];
          const bn = p2 + p3 + p4 + p5 + p6 + p7 + p8 + p9;
          if (bn < 2 || bn > 6) continue;
          const seq = [p2, p3, p4, p5, p6, p7, p8, p9, p2];
          let a = 0;
          for (let k = 0; k < 8; k++) if (seq[k] === 0 && seq[k + 1] === 1) a++;
          if (a !== 1) continue;
          if (step === 0) {
            if (p2 * p4 * p6 !== 0) continue;
            if (p4 * p6 * p8 !== 0) continue;
          } else {
            if (p2 * p4 * p8 !== 0) continue;
            if (p2 * p6 * p8 !== 0) continue;
          }
          toClear.push(i);
        }
      }
      if (toClear.length) {
        changed = true;
        for (const i of toClear) m[i] = 0;
      }
    }
  }

  // Drop isolated skeleton points (lone specks) so they vanish instead of
  // re-dilating into blobs; connected wall skeletons are untouched.
  for (let y = 0; y < h; y++)
    for (let x = 0; x < w; x++)
      if (m[y * w + x] && !anyNeighbour(m, w, h, x, y)) m[y * w + x] = 0;

  // Re-dilate the skeleton to the target width.
  let sk = m;
  for (let k = 0; k < halfWidth; k++) sk = dilate(sk, w, h);

  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const i = y * w + x;
      const was = data[i] < OCC_THRESHOLD;
      const now = sk[i] === 1;
      if (now) data[i] = OCCUPIED;
      else if (was) data[i] = blendBackground(data, w, h, x, y);
    }
  }
}

/** Editor zoom range: 1 = fits the wrapper width, up to 6x for fine brush work. */
export const MIN_ZOOM = 1;
export const MAX_ZOOM = 6;

export function clampZoom(z: number): number {
  return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z));
}

/** Multiply the current zoom by `factor` and clamp (buttons use a fixed factor, wheel a continuous one). */
export function zoomBy(current: number, factor: number): number {
  return clampZoom(current * factor);
}
